import { useLayoutEffect, useMemo, useRef, type ReactNode } from 'react'
import { actionSheet } from '@/components/ui/ActionSheet'
import { getActionSheetSnapshot } from '@/components/ui/ActionSheet/action-sheet-store'

type SheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  children: ReactNode
  footer?: ReactNode
  className?: string
  dismissible?: boolean
  size?: 'auto' | 'large'
}

export function Sheet({ open, onOpenChange, title, children, footer, className, dismissible = true, size = 'auto' }: SheetProps) {
  const content = useMemo(() => <div className={className}>{children}</div>, [className, children])
  const id = useRef<number | null>(null)
  const onOpenChangeRef = useRef(onOpenChange)
  useLayoutEffect(() => { onOpenChangeRef.current = onOpenChange }, [onOpenChange])

  useLayoutEffect(() => {
    if (open) {
      const sheetId = actionSheet.open({
        title,
        content,
        footer,
        dismissible,
        size,
        onClose: () => {
          if (id.current !== sheetId) return
          id.current = null
          onOpenChangeRef.current(false)
        },
      })
      id.current = sheetId
    } else if (id.current === getActionSheetSnapshot().options?.id) {
      actionSheet.close()
    }
    return () => {
      if (id.current === getActionSheetSnapshot().options?.id) actionSheet.close()
      id.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useLayoutEffect(() => {
    const options = getActionSheetSnapshot().options
    if (open && id.current === options?.id && (
      options.title !== title || options.content !== content || options.footer !== footer ||
      options.dismissible !== dismissible || options.size !== size
    )) {
      actionSheet.update({ title, content, footer, dismissible, size })
    }
  }, [open, title, content, footer, dismissible, size])

  return null
}
